import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { TopicWizard } from '@/components/topics/topic-wizard'
import { buttonVariants } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { reportQuery, subaccountEntitlementsQuery, tagsQuery, topicsQuery } from '@/lib/queries'
import { rtaQuery } from '@/lib/rta-queries'
import { draftFromRecord, useTopicStore } from '@/lib/topic-store'
import { cn } from '@/lib/utils'

/**
 * Optional return target: when a report's Headers tab opens this editor, it carries the account
 * and report so save/cancel land back on that report's Headers tab. Both absent (the normal path
 * from the topics list) → the wizard returns to the client's topics page.
 */
const editSearchSchema = z.object({
  returnAccountId: z.string().optional(),
  returnReportId: z.string().optional(),
  /** Opened from an RTA's Topics tab (CLI-744): save/cancel go back to that RTA. */
  returnRtaId: z.string().optional(),
})

export const Route = createFileRoute(
  '/app/accounts/$accountId_/topics/$subaccountId/$topicId/edit',
)({
  validateSearch: editSearchSchema,
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(topicsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(tagsQuery(params.subaccountId)),
      context.queryClient.ensureQueryData(
        subaccountEntitlementsQuery(params.accountId, params.subaccountId),
      ),
    ]),
  component: EditTopic,
})

function EditTopic() {
  const { accountId, subaccountId, topicId } = Route.useParams()
  const { returnAccountId, returnReportId, returnRtaId } = Route.useSearch()
  const returnRta = useQuery({ ...rtaQuery(returnRtaId ?? ''), enabled: !!returnRtaId })
  const returnToRta = returnRtaId
    ? {
        accountId,
        rtaId: returnRtaId,
        ...(returnRta.data?.name ? { rtaName: returnRta.data.name } : {}),
      }
    : undefined
  // Fetch the report only to name the Back button; warm from the report page that sent us here.
  const returnReport = useQuery({
    ...reportQuery(returnReportId ?? ''),
    enabled: !!returnReportId,
  })
  const returnTo =
    returnAccountId && returnReportId
      ? {
          accountId: returnAccountId,
          reportId: returnReportId,
          ...(returnReport.data?.name ? { reportName: returnReport.data.name } : {}),
        }
      : undefined
  const { topics, updateTopic } = useTopicStore(subaccountId)
  const topic = topics.find((item) => item.id === topicId)

  if (!topic) {
    return (
      <div className="mx-auto w-full max-w-2xl animate-fade-in">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <p className="text-sm text-muted-foreground">
              That topic doesn't exist — it may have been deleted.
            </p>
            <Link
              to="/app/accounts/$accountId/topics/$subaccountId"
              params={{ accountId, subaccountId }}
              className={cn(buttonVariants())}
            >
              Back to topics
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <TopicWizard
      accountId={accountId}
      subaccountId={subaccountId}
      initialDraft={draftFromRecord(topic)}
      heading="Edit topic"
      submitLabel="Save changes"
      topicId={topic.id}
      returnTo={returnTo}
      returnToRta={returnToRta}
      onSubmit={(draft) => updateTopic(topic.id, draft)}
    />
  )
}
